import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useDebounce } from '@repo/react-hooks/useDebounce';

import { InputField } from './styles';
import { TextInputElement, TextInputProps, TextInputTheme } from './types';
import { getSelectionRange, SelectionRange } from './utils';

type OnlyTextInputFields =
  | 'infoText'
  | 'icon'
  | 'errorMessage'
  | 'dataTestId'
  | 'isPasswordInput'
  | 'rightIcon'
  | 'rightButton'
  | 'onTogglePasswordVisibility'
  | 'activeOnHover'
  | 'activeOnFocus'
  | 'theme';

interface InternalInputProps extends Omit<TextInputProps, OnlyTextInputFields> {
  hasRightMargin: boolean;
  type: 'password' | 'text';
  inputName?: string;
  theme: TextInputTheme;
}

const DEFAULT_DEBOUNCE = 2000;

const InternalInput: React.FC<InternalInputProps> = ({
  maxLength,
  placeholder = '',
  blurOnEnter = true,
  ...props
}) => {
  const [currentValue, setCurrentValue] = useState(props.value || '');
  const [selectionRange, setSelectionRange] = useState<SelectionRange | null>(
    null
  );
  const [isEditing, setIsEditing] = useState(false);

  const inputRef = useRef<HTMLInputElement>();

  const setRef = (node: HTMLInputElement): void => {
    inputRef.current = node;
    if (props.inputRef) {
      if (typeof props.inputRef === 'function') {
        props.inputRef(node);
      } else {
        props.inputRef.current = node;
      }
    }
  };

  useEffect(() => {
    // if a value is set and editing is left, use the given value
    if (props.value !== undefined && !isEditing) {
      setCurrentValue(props.value || '');
    }
  }, [props.value, isEditing]);

  useEffect(() => {
    if (inputRef.current && selectionRange?.length) {
      inputRef.current.setSelectionRange(...selectionRange);
    }
  }, [selectionRange]);

  const onChangedDebounced = useDebounce({
    callback: (val: string) => {
      props.onDebounce?.(val);
    },
    delay: DEFAULT_DEBOUNCE,
  });

  const getValue = useCallback(
    (
      event:
        | React.KeyboardEvent<TextInputElement>
        | React.ChangeEvent<TextInputElement>
        | React.FocusEvent<TextInputElement>
        | React.ClipboardEvent
    ) => {
      let value = '';

      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        value = event.target.value;
      } else if (event.target instanceof HTMLDivElement) {
        value = event.target.innerText.trim() || placeholder || '';
      } else if (event instanceof ClipboardEvent) {
        value = event.currentTarget.innerHTML;
      }

      return value;
    },
    [placeholder]
  );

  const onInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newValue = getValue(event);

    if (newValue && props.pattern && !props.pattern.test(newValue)) {
      // Prevent moving cursor from the current position
      return setSelectionRange(
        getSelectionRange(event, newValue.length - currentValue.length)
      );
    }

    setCurrentValue(newValue);
    props.onChanging?.(newValue);
    onChangedDebounced(newValue);
  };

  const onInputFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
    setIsEditing(true);
    props.onFocus?.(e);
  };

  const onInputBlur = (event: React.FocusEvent<TextInputElement>): void => {
    const newValue = getValue(event);

    if (newValue && props.pattern && !props.pattern.test(newValue)) {
      return;
    }

    setCurrentValue(newValue);
    setIsEditing(false);
    props.onChanged?.(newValue);
    props.onBlur?.();
  };

  const onInputKeyDown = (
    event: React.KeyboardEvent<TextInputElement>
  ): void => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      setIsEditing(false);
      props.onEnter?.(getValue(event));
      if (blurOnEnter && event.target instanceof HTMLElement) {
        event.target.blur(); // blur input, triggers `onInputBlur`
      }
    }
    props.onKeyDown?.(event);
  };

  const onPaste = (event: React.ClipboardEvent): void => {
    const data = event.clipboardData.getData('text/plain');
    if (data && maxLength) {
      const selectionLength = window.getSelection()?.toString().length ?? 0;
      const currentContentLength = getValue(event).length;
      const copiedContentLength = data.length;
      if (
        currentContentLength - selectionLength + copiedContentLength >
        maxLength
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  };

  return (
    <InputField
      ref={setRef}
      $hasRightMargin={props.hasRightMargin}
      $isTextArea={props.isTextArea}
      $theme={props.theme ?? {}}
      autoComplete={props.autoComplete}
      data-testid="text-input--input"
      disabled={props.disabled}
      maxLength={maxLength}
      name={props.inputName ?? ''}
      placeholder={placeholder}
      spellCheck={props.spellCheck}
      type={props.type}
      value={currentValue}
      onBlur={onInputBlur}
      onChange={onInputChange}
      onFocus={onInputFocus}
      onKeyDown={onInputKeyDown}
      onPaste={onPaste}
    />
  );
};

export default InternalInput;
